"use client"

import React, { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { loadProducts } from "@/actions/productActions"
import { changeQuoteRequestToQuoteAction, updateQuoteRequestAction } from "@/actions/quoteActions"
import { QuoteRequestRO, QuoteRO } from "@ub/quoting-client"
import { useTranslations } from "next-intl"

import { formatDate, formatPrice } from "@/lib/helper"
import { generateProductLink } from "@/lib/link-utils/link-utils"
import { getSbLocaleFromIsoLocale } from "@/lib/locale-helper"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { determinePrefixedProductId } from "@/lib/storefront/product/productHelper"
import ImagePreviewGroup from "@/components/ui/components/image-preview-group"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

interface AccountQuoteClientProps {
  data: any
  isQuote: boolean
  accountContext: AccountContext
}

function QuoteItemRow({ item, product, idx, handleQuantityChange, accountContext }: any) {
  if (!product?.id) return null
  const variantLink = generateProductLink(
    product.title,
    product.id,
    undefined,
    getSbLocaleFromIsoLocale(accountContext.isoLocale)
  )
  return (
    <div className="grid grid-cols-[40px_1fr_3fr_1fr_1fr_1fr] items-center gap-2 border-b py-2 text-sm">
      <div className="flex items-center gap-2">
        <ImageWithFallback
          className={"aspect-square h-20 object-contain"}
          src={product.image ?? ""}
          width={80}
          height={80}
          alt={product.title}
        />
      </div>
      <div className="text-blue-600">
        <Link prefetch={false} className={"cursor-pointer"} href={variantLink}>
          <p>{" " + determinePrefixedProductId(product.id)}</p>
        </Link>
      </div>
      <div>{product.title}</div>
      <div>
        <div className="flex items-center gap-2">
          <button
            className="w-8 h-8 flex items-center justify-center border  hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation()
              handleQuantityChange(idx, (item.quantity?.value || 1) - 1)
            }}
          >
            -
          </button>
          <input
            type="number"
            className="w-16 text-center border  py-1"
            value={item.quantity?.value || 1}
            onChange={(e) => {
              handleQuantityChange(idx, parseInt(e.target.value) || 1)
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="w-8 h-8 flex items-center justify-center border  hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation()
              handleQuantityChange(idx, (item.quantity?.value || 1) + 1)
            }}
          >
            +
          </button>
        </div>
      </div>
      <div className="text-right">
        {formatPrice(accountContext.isoLocale, item.singlePrice?.value, item.singlePrice?.currency)}
      </div>
      <div className="text-right">
        {formatPrice(accountContext.isoLocale, item.totalPrice?.value, item.totalPrice?.currency)}
      </div>
    </div>
  )
}

export default function AccountQuoteClient({ data, isQuote, accountContext }: AccountQuoteClientProps) {
  const t = useTranslations("storefront")

  const [quoteData, setQuoteData] = useState(data)
  const [products, setProducts] = useState<any[]>([])

  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [creatingQuote, setCreatingQuote] = useState(false)
  const [quoteCreated, setQuoteCreated] = useState(false)
  useEffect(() => {
    const productSkus = !isQuote
      ? (quoteData as QuoteRequestRO).items?.map((item) => (item as any).productSKU) || []
      : (quoteData as QuoteRO).items?.map((item) => (item as any).productSKU) || []
    loadProducts(productSkus).then((loadedProducts) => setProducts(loadedProducts))
  }, [quoteData, isQuote])
  const handleSave = async () => {
    setSaving(true)
    setSaveSuccess(false)
    try {
      await updateQuoteRequestAction(accountContext.customerData, accountContext.userData, {
        id: quoteData.id,
        displayName: quoteData.displayName || "",
        description: quoteData.description || "",
        items: quoteData.items || [],
      })
      setSaveSuccess(true)
      window.location.reload()
    } finally {
      setSaving(false)
    }
  }

  const handleCreateQuote = async () => {
    setCreatingQuote(true)
    try {
      await changeQuoteRequestToQuoteAction(accountContext.customerData, accountContext.userData, quoteData.id)
      setQuoteCreated(true)
      window.location.reload()
    } finally {
      setCreatingQuote(false)
    }
  }

  const handleQuantityChange = (idx: number, newQuantity: number) => {
    newQuantity = Math.max(1, newQuantity)
    const updatedItems = [...(quoteData.items || [])]
    updatedItems[idx] = { ...updatedItems[idx], quantity: { ...updatedItems[idx].quantity, value: newQuantity } }
    setQuoteData({ ...quoteData, items: updatedItems })
  }
  if (!data) return <div>{t("common.not_found")}</div>
  return (
    <div className="bg-white shadow p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col items-center mb-6">
        <div className="flex gap-2 mb-2">
          <ImagePreviewGroup imageUrls={products?.map((p) => p?.image)}></ImagePreviewGroup>
        </div>
        <div className="text-2xl font-bold">
          {t("quote.process_number")}: {quoteData.number}
        </div>
        <div className="flex flex-col gap-4 mt-2">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 w-24">{t("quote.reference")}</label>
            <input
              className="border px-2 py-1 flex-1"
              value={quoteData.displayName || ""}
              onChange={(e) => setQuoteData((q) => ({ ...q, displayName: e.target.value }))}
              placeholder={t("quote.reference_placeholder")}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 w-24">{t("quote.comment")}</label>
            <input
              className="border px-2 py-1 flex-1 w-[20em]"
              value={quoteData.description || ""}
              onChange={(e) => setQuoteData((q) => ({ ...q, description: e.target.value }))}
              placeholder={t("quote.comment_placeholder")}
            />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="grid grid-cols-[40px_1fr_3fr_1fr_1fr_1fr] items-center gap-2 border-b pb-2 text-sm font-semibold">
        <div></div>
        <div>{t("product.article_number")}</div>
        <div>{t("product.single_price")}</div>
        <div>{t("product.quantity")}</div>
        <div>{t("product.single_price")}</div>
        <div>{t("product.total_price")}</div>
      </div>
      <div className="bg-gray-50  p-4 mb-6">
        {quoteData.items?.map((item: any, idx: number) => (
          <QuoteItemRow
            key={idx}
            item={item}
            product={products[idx] || {}}
            idx={idx}
            handleQuantityChange={handleQuantityChange}
            accountContext={accountContext}
          />
        ))}
      </div>

      {/* Summary */}
      <div className="flex flex-col items-end gap-1 mb-6">
        <div className="flex gap-8 text-lg">
          <div>{t("quote.total_net")}:</div>
          <div className="font-bold">
            {formatPrice(accountContext.isoLocale, quoteData.total?.value, quoteData.total?.currency)}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end">
        {!isQuote && (
          <>
            <button
              className="bg-primary text-white px-8 py-2 font-bold"
              onClick={handleCreateQuote}
              disabled={creatingQuote}
            >
              {creatingQuote ? t("quote.saving") : t("quote.create_quote")}
            </button>
            <button
              className="border-2 border-red-700 text-red-700 px-8 py-2 font-bold bg-white"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t("quote.saving") : t("quote.save_request")}
            </button>
          </>
        )}
      </div>
      {saveSuccess && <div className="text-green-600 mt-2">{t("quote.request_saved")}</div>}
      {quoteCreated && <div className="text-green-600 mt-2">{t("quote.quote_created")}</div>}
    </div>
  )
}
